import { MutableRefObject, useMemo, useState } from 'react'
import { message } from 'antd'
import { AiAgentMessage } from '../ai-agent.type'
import { AiAgentMessageBubble } from './AiAgentMessageBubble'
import { AiAgentLoadingMessage } from './AiAgentLoadingMessage'
import { AiAgentCommonQuestions } from './AiAgentCommonQuestions'
import { AiSendIcon } from '../assets/svgs/AiSendIcon'
import { AiAgentLogo } from './AiAgentLogo'
import { ContextType } from '../contexts/ai-agent.context'
import { ReactComponent as IntroIcon } from '../assets/intro-icon.svg'
import { AiAgentKeyDiffsWorkflow } from './AiAgentKeyDiffsWorkflow'
import { PolicyUploadControl, usePolicyUpload } from './PolicyUploadControl'
import { UploadIllustration, CopyIcon } from '../assets/svgs'
import { canUserUploadPolicyPdf } from '../policy-upload.access'
import { useAppSelector } from 'store'
import { copyEntireChat } from '../libs/clipboard.utils'

interface AiAgentChatBoxProps {
  initialMessages: AiAgentMessage[]
  messages: AiAgentMessage[]
  input: string
  isLoading: boolean
  isClosing: boolean
  chatEndRef: MutableRefObject<HTMLDivElement>
  inputRef: MutableRefObject<HTMLInputElement>
  onInputChange: (value: string) => void
  onInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  onSend: () => void
  onReset: () => void
  onClose: () => void
  onAnimationEnd: () => void
  showTimestamp?: boolean
  maxLength?: number
  inputError?: string | null
  onQuestionSelect: (question: string) => void
  contextToggles: Array<{
    type: ContextType | 'history'
    enabled: boolean
    label: string
    onChange: (enabled: boolean) => void
  }>
  isExpanded?: boolean
  onToggleExpand?: () => void
  isAvailable: boolean
  onRegenerate?: (messageId: string) => void
  regeneratingMessageIndex?: number | null
  onOpenIntro?: () => void
  isAdmin?: boolean
  selectedPolicyContents?: unknown[]
  selectedPolicyCodes?: string[]
  onOpenEmail?: ({ html, subject }: { html?: string; subject?: string }) => void
  featureToggles?: Array<{
    type: string
    enabled: boolean
    label: string
    adminOnly: boolean
    onChange: (enabled: boolean) => void
  }>
  onDownloadPdf?: ({ html, frontMatter }: { html: string; frontMatter: string }) => void
  onOpenPolicySelection?: () => void
}

function AiAgentChatBox({
  initialMessages,
  messages,
  input,
  isLoading,
  isClosing,
  chatEndRef,
  inputRef,
  onInputChange,
  onInputKeyDown,
  onSend,
  onReset,
  onClose,
  onAnimationEnd,
  showTimestamp,
  maxLength,
  inputError,
  onQuestionSelect,
  contextToggles,
  isExpanded,
  onToggleExpand,
  isAvailable,
  onRegenerate,
  regeneratingMessageIndex,
  onOpenIntro,
  isAdmin = false,
  selectedPolicyContents,
  selectedPolicyCodes,
  onOpenEmail,
  featureToggles = [],
  onDownloadPdf,
  onOpenPolicySelection,
}: AiAgentChatBoxProps) {
  const [showScrollButton, setShowScrollButton] = useState(false)
  const [showDisclaimer, setShowDisclaimer] = useState(true)

  // Upload state
  const uploadState = usePolicyUpload()

  // Open to everyone outside production; in production, admins and the allowlisted test
  // accounts. Read off the store rather than the isAdmin prop so this gate and the two in
  // the table cannot answer differently for the same user.
  const canUpload = useAppSelector(canUserUploadPolicyPdf)

  const hasTwoPoliciesSelected = useMemo(
    () => selectedPolicyContents?.length === 2,
    [selectedPolicyContents],
  )

  const getToggle = (type: ContextType | 'history') => contextToggles.find((t) => t.type === type)
  const isToggleEnabled = (type: ContextType | 'history') => getToggle(type)?.enabled ?? false
  const toggleContext = (type: ContextType | 'history') => {
    const toggle = getToggle(type)
    if (toggle) {
      toggle.onChange(!toggle.enabled)
    }
  }
  const renderToggleSwitch = (type: ContextType | 'history', label: string, tooltip: string) => (
    <div className='ai-agent-toggle-switch'>
      <span className='ai-agent-toggle-label'>{label}</span>
      <button
        type='button'
        className={`ai-agent-switch${isToggleEnabled(type) ? ' ai-agent-switch--active' : ''}`}
        onClick={() => toggleContext(type)}
        title={tooltip}
      >
        <span className='ai-agent-switch-slider'></span>
      </button>
    </div>
  )

  function handleScrollToBottom() {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  function handleChatScroll(e: React.UIEvent<HTMLDivElement>) {
    const target = e.target as HTMLDivElement
    const isScrolledUp = target.scrollTop < target.scrollHeight - target.clientHeight - 950
    setShowScrollButton(isScrolledUp && messages.length > 1)
  }

  function handleKeyDiffsGenerate() {
    if (!hasTwoPoliciesSelected) {
      onOpenPolicySelection?.()
      message.info('Select two policy wordings to run the key differences report.')
      return
    }
    onQuestionSelect('What are the key differences between the selected policies?')
  }

  function handleKeyDiffsSelectPolicies() {
    onOpenPolicySelection?.()
    const count = selectedPolicyContents?.length ?? 0
    if (count > 2) {
      message.info(
        'Please keep only two policy wordings selected to run the key differences report.',
      )
    }
  }

  return (
    <div
      className={`ai-agent-float-box${isClosing ? ' ai-agent-float-box--closing' : ''}${
        isExpanded ? ' ai-agent-float-box--expanded' : ''
      }`}
      onAnimationEnd={onAnimationEnd}
      onDrop={canUpload ? uploadState.handleDrop : undefined}
      onDragOver={canUpload ? uploadState.handleDragOver : undefined}
      onDragLeave={canUpload ? uploadState.handleDragLeave : undefined}
    >
      <div className='ai-agent-header'>
        <AiAgentLogo className='ai-agent-avatar' size={28} />
        <span>Ask AL</span>
        <div className='ai-agent-header-buttons'>
          {onOpenIntro && (
            <IntroIcon
              width={28}
              height={28}
              fill='currentColor'
              style={{
                color: '#3acce1',
                cursor: 'pointer',
                marginBottom: '4px',
                marginRight: '3px',
              }}
              onClick={onOpenIntro}
              aria-label='Show introduction'
              title='Show introduction'
            />
          )}
          {(initialMessages.length > 0 || messages.length > 0) && (
            <button
              type='button'
              className='ai-agent-header-button'
              onClick={() => copyEntireChat([...initialMessages, ...messages])}
              aria-label='Copy entire chat'
              title='Copy entire chat'
            >
              <CopyIcon size={16} />
            </button>
          )}
          {onToggleExpand && (
            <button
              type='button'
              className='ai-agent-header-button'
              onClick={onToggleExpand}
              title={isExpanded ? 'Collapse to compact view' : 'Expand to full screen'}
            >
              <span className='ai-agent-expand-icon'>{isExpanded ? '↘' : '⛶'}</span>
            </button>
          )}
          <button
            type='button'
            className='ai-agent-header-button'
            onClick={onReset}
            aria-label='Start new chat'
            title='Start new chat'
          >
            ↺
          </button>
          <button
            type='button'
            className='ai-agent-header-button'
            onClick={onClose}
            aria-label='Collapse chatbox'
            title='Collapse chatbox'
          >
            ×
          </button>
        </div>
      </div>

      <AiAgentCommonQuestions onQuestionSelect={onQuestionSelect} isLoading={isLoading} />

      <div className='ai-agent-chat' onScroll={handleChatScroll}>
        {showDisclaimer && (
          <div className='ai-agent-disclaimer' role='note' aria-label='AI assistant disclaimer'>
            <button
              type='button'
              className='ai-agent-disclaimer-close'
              aria-label='Dismiss disclaimer'
              title='Dismiss'
              onClick={() => setShowDisclaimer(false)}
            >
              ×
            </button>
            <span>
              This AI assistant is in early access and still under active development. Please review
              responses carefully and use your judgment before acting on them.
            </span>
          </div>
        )}

        {initialMessages.length > 0 &&
          initialMessages.map((msg, idx) => (
            <AiAgentMessageBubble
              key={msg.id}
              msg={msg}
              idx={idx}
              isInitial={true}
              messages={initialMessages}
              showTimestamp={!!showTimestamp}
              onRegenerate={onRegenerate}
              onDownloadPdf={onDownloadPdf}
              onOpenEmail={onOpenEmail}
            />
          ))}
        {isAvailable &&
          messages.map((msg, idx) => (
            <AiAgentMessageBubble
              key={msg.id}
              msg={msg}
              idx={idx}
              messages={messages}
              showTimestamp={!!showTimestamp}
              onRegenerate={onRegenerate}
              hasTwoPoliciesSelected={hasTwoPoliciesSelected}
              onDownloadPdf={onDownloadPdf}
              onOpenEmail={onOpenEmail}
            />
          ))}

        {isAvailable && messages.length === 0 && (
          <AiAgentKeyDiffsWorkflow
            selectedPolicyCount={selectedPolicyContents?.length ?? 0}
            selectedPolicyCodes={selectedPolicyCodes ?? []}
            onGenerateReport={handleKeyDiffsGenerate}
            onSelectPolicies={handleKeyDiffsSelectPolicies}
            isLoading={isLoading}
          />
        )}
        {isLoading && (
          <AiAgentLoadingMessage
            showText={true}
            text={regeneratingMessageIndex ? 'AL is regenerating...' : 'AL is generating...'}
          />
        )}
        <div ref={chatEndRef} />
        {showScrollButton && (
          <button
            className='ai-agent-scroll-to-bottom'
            onClick={handleScrollToBottom}
            aria-label='Scroll to end of chat'
            title='Scroll to end of chat'
          >
            View end of chat
          </button>
        )}
      </div>

      <div className='ai-agent-toggles'>
        <div className='ai-agent-toggles-left'>
          {renderToggleSwitch(
            'filters',
            'Filters',
            isToggleEnabled('filters')
              ? 'Exclude filter settings from AI responses'
              : 'Include filter settings in AI responses',
          )}
          {renderToggleSwitch(
            'history',
            'Chat History',
            isToggleEnabled('history')
              ? 'Exclude conversation history from AI responses'
              : 'Include conversation history in AI responses',
          )}
        </div>
        {canUpload && (
          <div className='ai-agent-toggles-right'>
            <PolicyUploadControl variant='toggle' uploadState={uploadState} />
          </div>
        )}
      </div>

      {/* Admin-only feature toggles */}
      {isAdmin && featureToggles.length > 100 && (
        <div className='ai-agent-toggles ai-agent-toggles--admin'>
          <div className='ai-agent-toggle-section-label'>
            <span className='ai-agent-admin-badge'>Admin</span>
            <span>Feature Toggles</span>
          </div>
          {featureToggles.map((toggle) => (
            <div key={toggle.type} className='ai-agent-toggle-switch'>
              <span className='ai-agent-toggle-label'>{toggle.label}</span>
              <button
                type='button'
                className={`ai-agent-switch${toggle.enabled ? ' ai-agent-switch--active' : ''}`}
                onClick={() => toggle.onChange(!toggle.enabled)}
                title={`${toggle.enabled ? 'Disable' : 'Enable'} ${toggle.label}`}
              >
                <span className='ai-agent-switch-slider'></span>
              </button>
            </div>
          ))}
        </div>
      )}

      <div className='ai-agent-input-row'>
        <input
          ref={inputRef}
          className='ai-agent-input'
          value={input}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={onInputKeyDown}
          placeholder='Enter a message ...'
          maxLength={maxLength}
        />

        <button
          className='ai-agent-send-btn'
          title='Send message'
          onClick={onSend}
          disabled={isLoading || !input.trim() || !!inputError || !isAvailable}
          aria-label='Send message'
        >
          <AiSendIcon />
        </button>
      </div>
      {inputError && <div className='ai-agent-input-error'>{inputError}</div>}

      {canUpload && uploadState.isDragActive && (
        <div className={`ai-agent-drop-overlay${uploadState.isDragActive ? ' is-over' : ''}`}>
          <div className='ai-agent-drop-message'>
            <UploadIllustration className='ai-agent-drop-icon' />
            <span>Drop your policy PDF here</span>
            <small>Supports PDF only (10MB max)</small>
          </div>
        </div>
      )}
    </div>
  )
}

export { AiAgentChatBox }
